import React, { useState } from 'react';
import CommonLayout from '../../../layouts/Common';
import CommonTable from '../../../components/Common/Table';
import { ActionBtn, ActionBtnWrapper, HeaderData, NameData, UrlData } from './style';
import { ColumnsType } from 'antd/es/table';
import { DataType } from './type';
import useListAnalysesView, { useToggleProfessionalViewable } from '../../../hooks/admin/useListAnalysesView';
import useGetAllProfessionals from '../../../hooks/professionals/useGetAllProfessionals';
import { formatDate } from '../../../helpers';
import IodineTestForm from '../../../components/Forms/Admin/IodineTest';
import { Spin, App } from 'antd';
import { useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { ADMIN_TOGGLE_CLIENT_VIEWABLE } from '../../../services/graphql/mutations/admin';
import { useAdminSignInAsProfessional } from '../../../hooks/admin/useAdminProfessional';

const IodineTest = () => {
  const token = useSelector((state: any) => state.auth.token);
  const professionals = useSelector((state: any) => state.professionals.professionals || []);
  const [toggleClientMutate] = useMutation(ADMIN_TOGGLE_CLIENT_VIEWABLE);
  const { signInAsProfessional } = useAdminSignInAsProfessional();
  const { message } = App.useApp();

  useGetAllProfessionals(); // Fetch professionals for ID lookup
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { data: iodineTestData, total, loading, perPage, refetch } = useListAnalysesView('iodine', currentPage as number);
  const { toggle, loading: toggleLoading } = useToggleProfessionalViewable();
  const [openNewIodineTest, setOpenNewIodineTest] = useState<boolean>(false);
  const [openEditIodineTest, setOpenEditIodineTest] = useState<boolean>(false);

  const onToggle = async (record: any) => {
    const analysesId = record.analysesId || record.id;
    const selectedProf = professionals?.find((p: any) => p.code === record.professionalCode);
    const professionalId = selectedProf?.id || record.professionalId;
    const clientId = parseInt(record.clientGid, 10);

    if (!analysesId || !professionalId || isNaN(clientId)) {
      message.error('Selection metadata missing.');
      return;
    }

    try {
      // Toggle Professional Viewable
      await toggle({
        analysesId: analysesId,
        analysesType: 'iodine',
        professionalId: professionalId
      });

      // Toggle Client Viewable (Syncing for Professional dashboard visibility)
      // Admins are naturally restricted from this mutation, so we assume the Professional's role
      if (selectedProf?.email) {
        try {
          const proSignIn = await signInAsProfessional(selectedProf.email);
          if (proSignIn?.token) {
            await toggleClientMutate({
              variables: {
                input: {
                  analysesId: analysesId,
                  analysesType: 'iodine',
                  clientId: record.clientId || clientId.toString()
                }
              },
              context: {
                headers: { Authorization: `Bearer ${proSignIn.token}` }
              }
            });
          }
        } catch (syncErr) {
          console.warn('Sync toggleClientViewable failed: ', syncErr);
        }
      }

      await refetch();
      message.success('Visibility updated successfully');
    } catch (err: any) {
      console.error('onToggle Error: ', err);
      message.error(`Failed to update visibility: ${err?.message || 'Error'}`);
    }
  }

  const columns: ColumnsType<any> = [
    {
      title: <HeaderData>Client</HeaderData>,
      render: (text, record) => (
        <NameData>{`${record.client || ''}`}</NameData>
      ),
    },
    {
      title: 'GID',
      render: (text, record) => (
        <NameData>{`${record.clientGid || ''}`}</NameData>
      ),
    },
    {
      title: <HeaderData>Professional</HeaderData>,
      render: (text, record) => (
        <NameData>{`${record.professional || ''}`}</NameData>
      ),
    },
    {
      title: 'Code',
      render: (text, record) => (
        <NameData>{`${record.professionalCode || ''}`}</NameData>
      ),
    },
    {
      title: <HeaderData>Added by</HeaderData>,
      render: (text, record) => (
        <NameData>{`${record.addedBy || 'Admin'}`}</NameData>
      )
    },
    {
      title: <HeaderData>Date</HeaderData>,
      render: (text, record) => (
        <UrlData>{`${formatDate(record.date || record.createdAt)}`}</UrlData>
      ),
    },
    {
      title: <HeaderData>Actions</HeaderData>,
      render: (text, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <ActionBtn
            onClick={() => onToggle(record)}
            active={record.professionalViewable}
            type="text"
            icon={
              <i className="material-icons-outlined" style={{ fontSize: 'inherit' }}>policy</i>
            }
          >
            {record.professionalViewable ? 'Disable' : 'Enable'}
          </ActionBtn>
        </div>
      ),
    }
  ];

  const handleBack = () => {
    setOpenNewIodineTest(false);
    setOpenEditIodineTest(false);
  };

  return (
    <CommonLayout title="Iodine Tests" width="1000px">
      {loading ? <Spin size="large" /> :
        <CommonTable
          showNewModal={openNewIodineTest}
          onNewData={setOpenNewIodineTest}
          showEditModal={openEditIodineTest}
          onEditData={setOpenEditIodineTest}
          handleBack={handleBack}
          modalForm={<IodineTestForm />}
          modalWidth={1100}
          columns={columns}
          dataSource={iodineTestData}
          tableType="Iodine Tests"
          total={`${total} rows`}
          page={currentPage}
          setCurrentPage={setCurrentPage}
          perPage={perPage}
          totalData={total}
        />
      }
    </CommonLayout>
  )
};

export default IodineTest;